/**
 * Publikacja — EKRAN DECYZJI + ASYSTENT PUBLIKACJI
 * Post = bohater. Krok po kroku. Zero chaosu.
 * To nie jest "kopiuj-wklej tool" — to jest asystent publikacji.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Sparkles, Loader2, CheckCircle2, Globe, Users,
  ExternalLink, Plus, Trash2, MapPin, Briefcase,
  Pencil, ImageIcon, RefreshCw, ChevronRight, ArrowRight,
  X, Smartphone, Camera, Target, AlertTriangle, Zap, PartyPopper, Mail, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PanelLayout from '@/components/PanelLayout';
import ContextPanel from '@/components/ContextPanel';
import Paywall from '@/components/Paywall';
import MediaPicker from '@/components/MediaPicker';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import { trackEvent } from '@/utils/tracking';

const REASON_META = {
  lokalizacja: { icon: MapPin, badge: 'Lokalna', color: '#10B981' },
  'branżowa': { icon: Briefcase, badge: 'Branżowa', color: '#3B82F6' },
  'szeroki zasięg': { icon: Globe, badge: 'Duży zasięg', color: '#F47A3B' },
  ogólna: { icon: Globe, badge: 'Popularna', color: '#F47A3B' },
  biznesowa: { icon: Briefcase, badge: 'Biznesowa', color: '#F47A3B' },
  rasa: { icon: Sparkles, badge: 'Rasowa', color: '#EC4899' },
};

const fmtMembers = (n) => {
  if (!n) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`;
};

/* ═══════════════════════════════════════════════════ */
/* ═══ STEP-BY-STEP WIZARD ═══ */
/* ═══════════════════════════════════════════════════ */
function PublishWizard({ groups, post, onClose, onMarkPosted, onFinish }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [pasted, setPasted] = useState(new Set());
  const current = groups[step] || null;
  const total = groups.length;
  const isDone = step >= total || !current;

  const handleOpenAndCopy = async () => {
    if (!post?.text || !current) return;
    try { await navigator.clipboard.writeText(post.text); } catch {
      const ta = document.createElement('textarea'); ta.value = post.text;
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    window.open(current.url, '_blank');
  };

  const handlePasted = () => {
    if (!current) return;
    setPasted(prev => new Set([...prev, current.id]));
    onMarkPosted(current.id);
    if (step + 1 < total) {
      setTimeout(() => setStep(s => s + 1), 600);
    } else {
      setTimeout(() => setStep(total), 600);
    }
  };

  const handleSkip = () => {
    if (step + 1 < total) setStep(s => s + 1);
    else setStep(total);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      data-testid="publish-wizard"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl w-[90vw] max-w-[480px] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-[#F47A3B]" />
            <h2 className="text-[15px] font-bold text-zinc-900">
              {isDone ? 'Gotowe!' : `Krok ${step + 1} z ${total}`}
            </h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600" data-testid="wizard-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-zinc-100">
          <div
            className="h-full bg-[#F47A3B] transition-all duration-500"
            style={{ width: `${isDone ? 100 : ((step) / total) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {isDone ? (
            /* ── DONE ── */
            <div className="text-center py-4" data-testid="wizard-done">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              </div>
              <h3 className="text-[18px] font-bold text-zinc-900 mb-2">
                Gotowe — Twoja oferta jest już widoczna
              </h3>
              <p className="text-[13px] text-zinc-500 mb-1">
                Opublikowano w {pasted.size} {pasted.size === 1 ? 'grupie' : 'grupach'}
              </p>
              <p className="text-[12px] text-emerald-600 font-medium mb-5">
                Sprawdź za chwilę czy ktoś odpisał
              </p>
              <Button
                onClick={onFinish}
                className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl px-8 h-11 text-[14px] font-bold"
                data-testid="wizard-finish"
              >
                Gotowe <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            /* ── STEP ── */
            <div data-testid={`wizard-step-${step}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#F47A3B]" />
                </div>
                <div>
                  <p className="text-[16px] font-bold text-zinc-900">{current?.name || 'Grupa'}</p>
                  <p className="text-[12px] text-zinc-400">Grupa {step + 1} z {total}</p>
                </div>
              </div>

              {/* Post mini-preview */}
              <div className="bg-[#F8FAFC] rounded-xl p-3 mb-5 border border-zinc-100">
                <p className="text-[12px] text-zinc-600 line-clamp-3">{post?.text?.slice(0, 150)}...</p>
                {post?.image_url && (
                  <img src={getImageUrl(post.image_url)} alt="" className="w-full h-24 object-cover rounded-lg mt-2" />
                )}
              </div>

              {pasted.has(current?.id) ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl mb-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-[14px] font-semibold text-emerald-700">Opublikowano</span>
                </div>
              ) : (
                <>
                  {/* Step 1: Open group */}
                  <Button
                    onClick={handleOpenAndCopy}
                    className="w-full bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl h-11 text-[14px] font-bold mb-3"
                    data-testid="wizard-open-group"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Otwórz grupę i wklej post
                  </Button>
                  <p className="text-[11px] text-zinc-400 text-center mb-3">
                    Post został skopiowany — wklej go w grupie i kliknij "Opublikuj"
                  </p>
                  <p className="text-[11px] text-amber-600 text-center mb-4 bg-amber-50 rounded-lg py-2 px-3">
                    Pamietaj: musisz byc czlonkiem grupy, zeby moc publikowac. Jesli widzisz blad — dolacz najpierw do grupy na Facebooku.
                  </p>

                  {/* Step 2: Mark as done */}
                  <Button
                    onClick={handlePasted}
                    variant="outline"
                    className="w-full rounded-xl h-10 text-[13px] font-semibold border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="wizard-mark-done"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Opublikowałem — następna grupa
                  </Button>
                </>
              )}

              {/* Skip */}
              <button
                onClick={handleSkip}
                className="w-full text-center text-[12px] text-zinc-400 hover:text-zinc-600 mt-3 py-1"
                data-testid="wizard-skip"
              >
                Pomiń tę grupę
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════ */
/* ═══ GROUP ROW COMPONENT ═══ */
/* ═══════════════════════════════════════════════════ */
function GroupRow({ g, isPosted, isSelected, onToggle, onDelete }) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
        isPosted ? 'bg-[#F0FDF4] border-emerald-200' :
        isSelected ? 'bg-[#FFF7ED] border-[#F47A3B]/20' :
        'bg-white border-zinc-200'
      }`}
      data-testid={`pub-group-${g.id}`}
    >
      <button
        onClick={() => !isPosted && onToggle(g.id)}
        className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          isPosted ? 'bg-emerald-500 border-emerald-500' :
          isSelected ? 'bg-[#F47A3B] border-[#F47A3B]' : 'border-zinc-300 hover:border-[#F47A3B]'
        }`}
      >
        {(isSelected || isPosted) && <CheckCircle2 className="w-4 h-4 text-white" />}
      </button>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => !isPosted && onToggle(g.id)}>
        <p className="text-[13px] font-semibold text-zinc-900 truncate">{g.name}</p>
        {isPosted && <p className="text-[10px] text-emerald-600 font-medium">Opublikowano</p>}
      </div>
      {!isPosted && g.url && (
        <a href={g.url} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-[#1877F2] bg-[#1877F2]/5 hover:bg-[#1877F2]/10 border border-[#1877F2]/10 transition-all flex-shrink-0"
          data-testid={`join-group-${g.id}`}
        >
          <ExternalLink className="w-3 h-3" /> Dołącz
        </a>
      )}
      <button onClick={() => onDelete(g.id)} className="text-zinc-300 hover:text-red-400 p-1 flex-shrink-0">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════ */
/* ═══ MAIN PAGE ═══ */
/* ═══════════════════════════════════════════════════ */
export default function Publikacja() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Post
  const [post, setPost] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [hasPhotos, setHasPhotos] = useState(true);
  const [loading, setLoading] = useState(true);
  const [profileLabel, setProfileLabel] = useState('');
  const [showHashtags, setShowHashtags] = useState(false);

  // Groups
  const [groups, setGroups] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [postedGroups, setPostedGroups] = useState(new Set());
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupUrl, setNewGroupUrl] = useState('');
  const [fbCampaign, setFbCampaign] = useState(null);
  const [fbCampaignStats, setFbCampaignStats] = useState(null);

  // Drawers & Wizard
  const [showEditText, setShowEditText] = useState(false);
  const [editText, setEditText] = useState('');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [quickMode, setQuickMode] = useState(false);
  const [showFirstWin, setShowFirstWin] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false); // Szybka publikacja
  const [publishedSuccess, setPublishedSuccess] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Promo
  const [promoOptions, setPromoOptions] = useState([]);

  // ── FETCH ──
  const fetchGroups = useCallback(async () => {
    try {
      const [groupsRes, profileRes, savedPostRes, subRes] = await Promise.all([
        axios.get(`${API}/groups/list`, { headers }).catch(() => ({ data: { groups: [], suggestions: [], has_photos: true } })),
        axios.get(`${API}/profile`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/groups/latest-post`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/subscription/status`, { headers }).catch(() => ({ data: { active: true } })),
      ]);
      const allGroups = groupsRes.data?.groups || [];
      setGroups(allGroups);
      setSuggestions(groupsRes.data?.suggestions || []);
      setHasPhotos(groupsRes.data?.has_photos ?? true);
      setSubscription(subRes.data || { active: true });

      // Select only 2-3 best groups, not all
      const bestCount = Math.min(3, allGroups.length);
      setSelectedGroups(new Set(allGroups.slice(0, bestCount).map(g => g.id)));

      // Load saved post if available
      if (savedPostRes.data && savedPostRes.data.text) {
        setPost(savedPostRes.data);
        setPromoOptions(savedPostRes.data.available_options || []);
      }

      // Build friendly label from profile (context_label from backend)
      const prof = profileRes.data;
      if (prof) {
        // Marketing mode — override label
        const meRes = await axios.get(`${API}/auth/me`, { headers }).catch(() => null);
        const contentMode = meRes?.data?.content_mode;
        if (contentMode === 'marketing') {
          setProfileLabel('ApisAI — Posty sprzedażowe');
        } else if (prof.context_label) {
          setProfileLabel(prof.context_label);
        } else if (prof.business_name && !prof.business_name.includes('TEST_')) {
          setProfileLabel(prof.business_name);
        }
      }

      // Fetch FB campaign if exists
      try {
        const campRes = await axios.get(`${API}/fb-campaign/active`, { headers });
        if (campRes.data?.campaign) {
          setFbCampaign(campRes.data.campaign);
          const posts = campRes.data.posts || [];
          const pub = posts.filter(p => p.status === 'published');
          const sched = posts.filter(p => p.status === 'scheduled');
          setFbCampaignStats({ published: pub.length, scheduled: sched.length, total: posts.length });
        }
      } catch {}
    } catch {}
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Track page view
  useEffect(() => { trackEvent('view_post_editor'); }, []);

  // Check first-win status
  useEffect(() => {
    axios.get(`${API}/user/first-win-status`, { headers }).then(r => {
      if (r.data?.show_first_win) setShowFirstWin(true);
      if (r.data?.show_followup) setShowFollowup(true);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Auto-publish after returning from Stripe
  useEffect(() => {
    if (searchParams.get('auto') === 'publish' && post && groups.length > 0) {
      setShowWizard(true);
    }
  }, [subscription, post, groups.length]); // eslint-disable-line

  // Auto-generate
  useEffect(() => {
    if (!loading && hasPhotos && !post && !generating) handleGenerate();
  }, [loading, hasPhotos]); // eslint-disable-line

  // Pre-fill from Inspiracje z rynku
  useEffect(() => {
    const inspiredPost = location.state?.inspiredPost;
    if (inspiredPost && !loading) {
      setPost(prev => prev ? { ...prev, text: inspiredPost } : { text: inspiredPost, type: 'inspired' });
      toast.success(t('publish.inspirationLoaded'));
    }
  }, [loading]); // eslint-disable-line

  // ── GENERATE ──
  const handleGenerate = async (promoteType, promoteId) => {
    setGenerating(true);
    try {
      const body = {};
      if (promoteType) { body.promote_type = promoteType; body.promote_id = promoteId || null; }
      const res = await axios.post(`${API}/groups/generate-post`, body, { headers });
      setPost(res.data);
      setPromoOptions(res.data.available_options || []);
      setPostedGroups(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || t('publish.generateFailed'));
    } finally { setGenerating(false); }
  };

  // ── ACTIONS ──
  const handleSaveText = () => {
    setPost(prev => ({ ...prev, text: editText }));
    setShowEditText(false);
    toast.success(t('publish.textUpdated'));
    trackEvent('post_edited');
  };

  const handleMediaSelect = (item) => {
    setPost(prev => ({ ...prev, image_url: item.file_url || item.thumbnail_url }));
    setShowMediaPicker(false);
    toast.success('Zdjęcie zmienione');
  };

  const handleVersion = async (versionType) => {
    setShowVersions(false);
    setGenerating(true);
    try {
      const body = { version_style: versionType };
      if (post?.promote_type) { body.promote_type = post.promote_type; body.promote_id = post.promoted_id; }
      const res = await axios.post(`${API}/groups/generate-post`, body, { headers });
      setPost(res.data);
      toast.success(t('publish.newVersionReady'));
    } catch { toast.error(t('publish.failed')); }
    finally { setGenerating(false); }
  };

  const handleWhatsApp = () => {
    if (!post?.text) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(post.text)}`, '_blank');
  };

  const toggleGroup = (id) => {
    setSelectedGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleMarkPosted = async (groupId) => {
    try {
      await axios.post(`${API}/groups/mark-posted`, { group_id: groupId, post_id: post?.post_id }, { headers });
      setPostedGroups(prev => new Set([...prev, groupId]));
      trackEvent('post_published', { group_id: groupId });
      // Trigger first-win check after publication
      axios.get(`${API}/user/first-win-status`, { headers }).then(r => {
        if (r.data?.show_first_win) setShowFirstWin(true);
      }).catch(() => {});
    } catch {}
  };

  const handleAddSuggestion = async (sug) => {
    try {
      await axios.post(`${API}/groups/save`, { name: sug.name, url: sug.url }, { headers });
      fetchGroups();
      toast.success(`Dodano: ${sug.name}`);
    } catch {}
  };

  const handleSaveGroup = async () => {
    if (!newGroupName || !newGroupUrl) return;
    try {
      await axios.post(`${API}/groups/save`, { name: newGroupName, url: newGroupUrl }, { headers });
      setNewGroupName(''); setNewGroupUrl(''); setAddingGroup(false);
      fetchGroups();
      toast.success(t('publish.added'));
    } catch (err) { toast.error(err.response?.data?.detail || t('publish.failed')); }
  };

  const handleDeleteGroup = async (id) => {
    try {
      await axios.delete(`${API}/groups/${id}`, { headers });
      setGroups(prev => prev.filter(g => g.id !== id));
    } catch {}
  };

  const handleStartPublishing = async () => {
    const selected = groups.filter(g => selectedGroups.has(g.id));
    if (selected.length === 0) { toast.error(t('publish.selectGroup')); return; }
    // Paywall disabled — wszystko za free
    setShowWizard(true);
  };

  // ── LOADING ──
  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" />
        </div>
      </PanelLayout>
    );
  }

  const selectedGroupsList = groups.filter(g => selectedGroups.has(g.id));

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="publikacja-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8">
            <div className="max-w-[640px] mx-auto space-y-5 pb-40 md:pb-32">

              {/* ═══ 1. HEADER (standard layout) ═══ */}

              {/* FIRST WIN — gratulacje po pierwszej publikacji */}
              {showFirstWin && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-6 text-center relative"
                  data-testid="first-win-modal"
                >
                  <button
                    onClick={() => {
                      setShowFirstWin(false);
                      axios.post(`${API}/user/first-win-shown`, {}, { headers }).catch(() => {});
                    }}
                    className="absolute top-3 right-3 text-emerald-400 hover:text-emerald-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h2 className="text-xl font-bold text-zinc-900 mb-2">Twój post został opublikowany!</h2>
                  <p className="text-[14px] text-zinc-600 mb-1">To pierwszy krok do klientów.</p>
                  <p className="text-[14px] text-zinc-600 mb-3">Powiadomię Cię, gdy ktoś napisze.</p>
                  <p className="text-[12px] text-emerald-600 font-medium">
                    Średnio pierwsze zapytania pojawiają się w 24–72h
                  </p>
                </motion.div>
              )}

              {/* FOLLOW-UP 24h — brak zapytań, zachęta do kolejnego posta */}
              {showFollowup && !showFirstWin && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#FFF7ED] border border-[#F47A3B]/20 rounded-2xl p-5"
                  data-testid="followup-notification"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center shrink-0">
                      <Zap className="w-5 h-5 text-[#F47A3B]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[14px] font-bold text-zinc-900 mb-1">Brak zapytań? To normalne na start!</p>
                      <p className="text-[13px] text-zinc-600 mb-3">Spróbuj posta typu „problem" — opisz problem, który rozwiązujesz. To przyciąga klientów szybciej.</p>
                      <Button
                        onClick={() => {
                          setShowFollowup(false);
                          navigate('/panel/competitor-watch');
                        }}
                        size="sm"
                        className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl px-5 text-[13px] font-bold"
                        data-testid="followup-generate-btn"
                      >
                        <Zap className="w-4 h-4 mr-1" /> Wygeneruj post typu problem
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-2" data-testid="pub-header">
                {/* Info banner — jak to działa */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-left">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-blue-800">Jak dziala publikacja?</p>
                      <p className="text-[11px] text-blue-700 mt-0.5">System przygotuje Ci gotowy post. Klikasz "Opublikuj" — otwiera sie grupa na Facebooku, a tekst kopiuje sie do schowka. Wklejasz go (Ctrl+V) i klikasz "Opublikuj" na Facebooku. To trwa 30 sekund!</p>
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-[13px] font-semibold mb-4" style={{ borderColor: '#F47A3B40', color: '#F47A3B', background: '#FFF7ED' }}>
                  <Send className="w-3.5 h-3.5" /> Publikacja
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mb-2">
                  {post ? 'Wybraliśmy najlepszy post dla Ciebie' : 'Przygotuj post i zdobądź klientów'}
                </h1>
                <p className="text-[15px] text-zinc-500">
                  {post
                    ? (profileLabel ? t('publish.promoting', { label: profileLabel }) : t('publish.checkAndPublish'))
                    : t('publish.systemPreparesPost')}
                </p>
              </motion.div>

              {/* ═══ FB CAMPAIGN BANNER ═══ */}
              {fbCampaign && fbCampaignStats && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-[#0F172A] border border-[#F47A3B]/20"
                  data-testid="fb-campaign-banner"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#F47A3B]/15 flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 text-[#F47A3B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-white truncate">{fbCampaign.name}</p>
                    <p className="text-[11px] text-zinc-400">{fbCampaignStats.published} opublikowanych · {fbCampaignStats.scheduled} gotowych</p>
                  </div>
                  <Button
                    onClick={() => navigate('/panel/kampania')}
                    size="sm"
                    className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-lg h-8 px-3 text-[11px] font-semibold flex-shrink-0"
                    data-testid="fb-campaign-banner-cta"
                  >
                    Zobacz <ChevronRight className="w-3 h-3 ml-0.5" />
                  </Button>
                </motion.div>
              )}

              {/* ═══ SZYBKA PUBLIKACJA — preview mode ═══ */}
              {post && !generating && quickMode && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border-2 border-[#F47A3B]/30 overflow-hidden shadow-lg"
                  data-testid="quick-publish-preview"
                >
                  <div className="bg-[#FFF7ED] px-5 py-3 border-b border-[#F47A3B]/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#F47A3B]" />
                      <span className="text-[14px] font-bold text-zinc-800">Szybka publikacja</span>
                    </div>
                    <button onClick={() => setQuickMode(false)} className="text-zinc-400 hover:text-zinc-600 transition-colors" data-testid="quick-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Mini post preview */}
                  <div className="p-4">
                    <p className="text-[13px] text-zinc-700 leading-relaxed line-clamp-3 mb-3">{post.text?.slice(0, 180)}...</p>

                    {/* Auto-selected groups */}
                    <div className="space-y-1.5 mb-4">
                      <p className="text-[11px] text-zinc-400 font-medium">Wybrane grupy:</p>
                      {groups.filter(g => selectedGroups.has(g.id)).slice(0, 3).map(g => (
                        <div key={g.id} className="flex items-center gap-2 text-[12px]">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          <span className="text-zinc-700 font-medium">{g.name}</span>
                          {g.members && <span className="text-zinc-400">{fmtMembers(g.members)} czł.</span>}
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={() => { setQuickMode(false); handleStartPublishing(); }}
                      className="w-full bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl h-11 text-[14px] font-bold shadow-md shadow-[#F47A3B]/20"
                      data-testid="quick-publish-now"
                    >
                      <Send className="w-4 h-4 mr-2" /> Publikuj teraz
                    </Button>
                    <p className="text-[10px] text-zinc-400 text-center mt-2">Możesz też przewinąć niżej, żeby poprawić tekst lub zmienić grupy</p>
                  </div>
                </motion.div>
              )}

              {/* ═══ QUICK PUBLISH CTA — shown when user has a saved post, not in quick mode ═══ */}
              {post && !generating && !quickMode && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-[#F47A3B] to-[#E06A2B] rounded-2xl p-5 text-white"
                  data-testid="quick-publish-cta"
                >
                  <p className="text-[15px] font-bold mb-1">Masz gotowy post</p>
                  <p className="text-[13px] text-white/80 mb-3">Grupy i treść są gotowe — opublikuj jednym klikiem</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setQuickMode(true)}
                      className="bg-white text-[#F47A3B] hover:bg-zinc-50 rounded-xl px-6 h-10 font-bold text-[14px]"
                      data-testid="quick-publish-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Szybka publikacja
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {/* scroll to post */}}
                      className="text-white/80 hover:text-white hover:bg-white/10 rounded-xl px-4 h-10 text-[13px]"
                    >
                      Chcę poprawić
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* ═══ 2. TOP BOX (no saved post) ═══ */}
              {!post && !generating && hasPhotos && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-[#F47A3B] to-[#E06A2B] rounded-2xl p-5 text-white"
                  data-testid="pub-topbox"
                >
                  <p className="text-[15px] font-bold mb-3">Masz gotowy post — opublikuj go i zdobądź klientów</p>
                  <Button
                    onClick={() => handleGenerate()}
                    className="bg-white text-[#F47A3B] hover:bg-zinc-50 rounded-xl px-6 h-10 font-bold text-[14px]"
                    data-testid="pub-main-cta"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> Opublikuj teraz
                  </Button>
                </motion.div>
              )}

              {/* Generating */}
              {generating && !post && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="bg-white rounded-2xl border border-zinc-200 p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B] mx-auto mb-3" />
                  <p className="text-[14px] font-semibold text-zinc-700">Przygotowuję Twój post...</p>
                  <p className="text-[12px] text-zinc-400 mt-1">System dobiera tekst i zdjęcie do Twojej oferty</p>
                </motion.div>
              )}

              {/* ═══ 3. POST — BOHATER ═══ */}
              {post && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="pub-post-hero">
                  {/* Promoting badge — friendly label */}
                  {profileLabel && (
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <div className="w-2 h-2 rounded-full bg-[#F47A3B]" />
                      <span className="text-[12px] text-zinc-500">Promujesz: <strong className="text-zinc-700">{profileLabel}</strong></span>
                    </div>
                  )}

                  {/* Facebook preview */}
                  <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)]" data-testid="fb-preview">
                    <div className="flex items-center gap-3 px-4 pt-4 pb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F47A3B] to-[#F9A66C] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {(user?.name || 'A').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-semibold text-[#050505]">Twoja oferta</p>
                        <div className="flex items-center gap-1 text-[12px] text-[#65676B]">
                          <span>Właśnie teraz</span><span>·</span><Globe className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-3">
                      <p className="text-[15px] text-[#050505] leading-relaxed whitespace-pre-line" data-testid="post-text">{post.text}</p>
                    </div>
                    {post.image_url && (
                      <img src={getImageUrl(post.image_url)} alt="" className="w-full max-h-[320px] object-cover border-t border-[#CED0D4]" data-testid="post-image" />
                    )}
                    <div className="px-4 py-3 border-t border-[#CED0D4]">
                      <p className="text-[11px] text-[#65676B] mb-2">Tak będzie wyglądał Twój post — możesz go poprawić przed publikacją</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setShowEditText(true)}
                          className="px-3 py-1.5 text-[12px] text-[#F47A3B] font-semibold border border-[#F47A3B]/30 rounded-lg hover:bg-[#F47A3B]/5"
                          data-testid="edit-post-btn"
                        >
                          Edytuj tekst
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              setGenerating(true);
                              const res = await axios.post(`${API}/ai/generate`, {
                                type: 'fb_post',
                                kennel_id: post?.kennel_id || null,
                                litter_id: post?.litter_id || null,
                                style: 'premium',
                              }, { headers });
                              const newText = res.data?.text || res.data?.content;
                              if (newText) {
                                setPost(prev => ({ ...prev, text: newText }));
                                toast.success('Nowy post wygenerowany!');
                              } else {
                                toast.error('Pusta odpowiedź — spróbuj ponownie');
                              }
                            } catch (err) {
                              console.error('AI generate error:', err?.response?.data || err);
                              toast.error('Błąd generowania — spróbuj edytować tekst ręcznie');
                            } finally {
                              setGenerating(false);
                            }
                          }}
                          disabled={generating}
                          className="px-4 py-2.5 text-[13px] text-blue-600 font-semibold border-2 border-blue-300 rounded-lg hover:bg-blue-50 active:scale-95 disabled:opacity-50 transition-all touch-manipulation"
                          data-testid="regenerate-post-btn"
                        >
                          {generating ? 'Generuję...' : 'Wygeneruj nowy'}
                        </button>
                        <button
                          onClick={() => {
                            setPost(null);
                            toast.info('Post usunięty — możesz wygenerować nowy');
                          }}
                          className="px-4 py-2.5 text-[13px] text-red-600 font-semibold border-2 border-red-300 rounded-lg hover:bg-red-50 active:scale-95 transition-all touch-manipulation"
                          data-testid="delete-post-btn"
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ═══ DECISION MESSAGE + QUALITY GATE (soft) ═══ */}
              {post && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                  className="bg-white border border-zinc-200 rounded-2xl px-5 py-4"
                  data-testid="quality-gate"
                >
                  <p className="text-[14px] font-semibold text-zinc-800 mb-3">
                    Gotowość do publikacji
                  </p>
                  <div className="space-y-2">
                    {(() => {
                      const text = post.text || '';
                      const hasCta = /napisz|zadzwoń|skontaktuj|wyślij|kliknij|sprawdź|zapytaj|zapraszam|link|ofert|\?/i.test(text);
                      const hasLength = text.length >= 30;
                      const hasImage = !!post.image_url;
                      const checks = [
                        { ok: hasLength, label: 'Długość tekstu', detail: hasLength ? `${text.length} znaków` : 'Za krótki (min. 30 znaków)' },
                        { ok: hasCta, label: 'CTA (wezwanie do działania)', detail: hasCta ? 'Post zachęca do kontaktu' : 'Dodaj pytanie lub zaproszenie do kontaktu' },
                        { ok: hasImage, label: 'Zdjęcie', detail: hasImage ? 'Dodano' : 'Zalecane — posty ze zdjęciem zdobywają 2x więcej reakcji' },
                      ];
                      return checks.map((c, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          {c.ok
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                            : <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                          }
                          <div>
                            <span className={`text-[13px] font-medium ${c.ok ? 'text-zinc-700' : 'text-amber-600'}`}>{c.label}</span>
                            <p className="text-[11px] text-zinc-400">{c.detail}</p>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                  {!post.image_url && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="add-image-section">
                      <p className="text-[13px] font-semibold text-amber-800 mb-2">Dodaj zdjęcie (zalecane)</p>
                      <p className="text-[11px] text-amber-600 mb-3">Posty ze zdjęciem zdobywają 2x więcej reakcji</p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowMediaPicker(true)}
                          size="sm"
                          variant="outline"
                          className="rounded-xl text-[12px] border-amber-300 text-amber-800 hover:bg-amber-100"
                          data-testid="add-image-gallery"
                        >
                          <ImageIcon className="w-3.5 h-3.5 mr-1" /> Wybierz z galerii
                        </Button>
                        <Button
                          onClick={() => setShowMediaPicker(true)}
                          size="sm"
                          variant="outline"
                          className="rounded-xl text-[12px] border-amber-300 text-amber-800 hover:bg-amber-100"
                          data-testid="add-image-system"
                        >
                          <Camera className="w-3.5 h-3.5 mr-1" /> Użyj grafiki systemu
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* ═══ 4. AKCJE ═══ */}
              {post && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="space-y-3" data-testid="pub-actions"
                >
                  {/* PRIMARY */}
                  <Button
                    onClick={handleStartPublishing}
                    disabled={selectedGroupsList.length === 0}
                    className="w-full rounded-xl h-12 text-[15px] font-bold text-white shadow-md"
                    style={{ background: 'linear-gradient(135deg, #F47A3B, #E06A2B)' }}
                    data-testid="pub-publish-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Opublikuj w wybranych grupach
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  {/* Inne kanały publikacji */}
                  {post?.text && (
                    <div className="bg-zinc-50 rounded-xl border border-zinc-200 p-3">
                      <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Opublikuj też na:</p>
                      <div className="grid grid-cols-3 gap-2">
                        <a
                          href="https://www.olx.pl/mojolx/nowe-ogloszenie/"
                          target="_blank" rel="noopener noreferrer"
                          onClick={() => { navigator.clipboard.writeText(post.text); toast.success('Tekst skopiowany! Wklej na OLX'); }}
                          className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg bg-white border border-zinc-200 hover:border-green-400 hover:bg-green-50 transition-all"
                          data-testid="channel-olx"
                        >
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <span className="text-[11px] font-semibold text-zinc-700">OLX</span>
                        </a>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(post.text)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg bg-white border border-zinc-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
                          data-testid="channel-whatsapp"
                        >
                          <Smartphone className="w-4 h-4 text-emerald-600" />
                          <span className="text-[11px] font-semibold text-zinc-700">WhatsApp</span>
                        </a>
                        <a
                          href={`mailto:?subject=${encodeURIComponent('Sprawdź moją ofertę')}&body=${encodeURIComponent(post.text)}`}
                          className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg bg-white border border-zinc-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                          data-testid="channel-email"
                        >
                          <Mail className="w-4 h-4 text-purple-600" />
                          <span className="text-[11px] font-semibold text-zinc-700">Email</span>
                        </a>
                      </div>
                    </div>
                  )}

                  {/* SECONDARY */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <button
                      onClick={() => { setEditText(post.text); setShowEditText(true); }}
                      className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border border-zinc-200 bg-white hover:border-[#F47A3B]/30 hover:bg-[#FFF7ED] transition-all"
                      data-testid="pub-edit-text"
                    >
                      <Pencil className="w-4 h-4 text-zinc-500" />
                      <span className="text-[12px] font-medium text-zinc-700">Edytuj tekst</span>
                    </button>
                    <button
                      onClick={() => setShowMediaPicker(true)}
                      className="flex flex-col items-center gap-1.5 py-4 px-2 rounded-xl border border-zinc-200 bg-white hover:border-[#F47A3B]/30 hover:bg-[#FFF7ED] active:scale-95 transition-all touch-manipulation"
                      data-testid="pub-change-photo"
                    >
                      <ImageIcon className="w-4 h-4 text-zinc-500" />
                      <span className="text-[12px] font-medium text-zinc-700">Zmień zdjęcie</span>
                    </button>
                    <button
                      onClick={() => setShowVersions(true)}
                      className="flex flex-col items-center gap-1.5 py-4 px-2 rounded-xl border border-zinc-200 bg-white hover:border-[#F47A3B]/30 hover:bg-[#FFF7ED] active:scale-95 transition-all touch-manipulation"
                      data-testid="pub-other-version"
                    >
                      <RefreshCw className="w-4 h-4 text-zinc-500" />
                      <span className="text-[12px] font-medium text-zinc-700">Inna wersja</span>
                    </button>
                    <button
                      onClick={() => setShowHashtags(!showHashtags)}
                      className={`flex flex-col items-center gap-1.5 py-4 px-2 rounded-xl border transition-all touch-manipulation active:scale-95 ${
                        showHashtags ? 'border-[#F47A3B]/30 bg-[#FFF7ED]' : 'border-zinc-200 bg-white hover:border-[#F47A3B]/30 hover:bg-[#FFF7ED]'
                      }`}
                      data-testid="pub-hashtags-toggle"
                    >
                      <span className="text-[16px]">#</span>
                      <span className="text-[12px] font-medium text-zinc-700">{showHashtags ? 'Ukryj' : 'Hashtagi'}</span>
                    </button>
                    <button
                      onClick={() => {
                        setPost(null);
                        toast.info('Post usunięty — możesz wygenerować nowy');
                      }}
                      className="flex flex-col items-center gap-1.5 py-4 px-2 rounded-xl border-2 border-red-300 bg-red-50 hover:border-red-500 hover:bg-red-100 active:scale-95 transition-all touch-manipulation"
                      data-testid="pub-delete-post"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                      <span className="text-[12px] font-semibold text-red-600">Usuń</span>
                    </button>
                  </div>

                  {/* Hashtag suggestions */}
                  {showHashtags && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      className="bg-white rounded-xl border border-zinc-200 p-3"
                      data-testid="pub-hashtags-panel"
                    >
                      <p className="text-[11px] text-zinc-500 mb-2">Kliknij, aby dodać do posta:</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          ...(profileLabel ? [`#${profileLabel.split(' ')[0]?.replace(/[^a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, '') || 'oferta'}`] : ['#oferta']),
                          `#${(post?.contact?.city || 'Polska').replace(/\s/g, '')}`,
                          '#zwierzęta',
                        ].map(tag => (
                          <button
                            key={tag}
                            onClick={() => {
                              if (!post.text.includes(tag)) {
                                setPost(prev => ({ ...prev, text: prev.text + `\n\n${tag}` }));
                                toast.success(`Dodano ${tag}`);
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg text-[12px] font-medium border border-zinc-200 text-zinc-600 hover:border-[#F47A3B]/30 hover:text-[#F47A3B] hover:bg-[#FFF7ED] transition-all"
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* WhatsApp */}
                  <button
                    onClick={handleWhatsApp}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#25D366]/20 bg-[#25D366]/5 text-[#128C7E] hover:bg-[#25D366]/10 transition-all text-[13px] font-semibold"
                    data-testid="pub-whatsapp"
                  >
                    <Smartphone className="w-4 h-4" /> Wyślij na telefon (WhatsApp)
                  </button>
                </motion.div>
              )}

              {/* ═══ PROGRESS TRACKER ═══ */}
              {post && postedGroups.size > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-zinc-200 p-4 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
                  data-testid="pub-progress-tracker"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Target className="w-4.5 h-4.5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[14px] font-bold text-zinc-900">
                        {postedGroups.size === groups.length
                          ? 'Opublikowano we wszystkich grupach!'
                          : `Opublikowano w ${postedGroups.size} z ${groups.length} grup`}
                      </p>
                      <p className="text-[12px] text-zinc-500">
                        {postedGroups.size === groups.length
                          ? 'Teraz czekaj na zapytania — odpowiadaj szybko'
                          : `Jeszcze ${groups.length - postedGroups.size} ${groups.length - postedGroups.size === 1 ? 'grupa' : 'grupy'} i masz pełne pokrycie!`}
                      </p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(postedGroups.size / Math.max(groups.length, 1)) * 100}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      className={`h-full rounded-full ${postedGroups.size === groups.length ? 'bg-emerald-500' : 'bg-[#F47A3B]'}`}
                    />
                  </div>
                </motion.div>
              )}

              {/* ═══ 5. GRUPY ═══ */}
              {post && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                  data-testid="pub-groups-section"
                >
                  <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-[#F47A3B]" />
                      <h2 className="text-[15px] font-bold text-zinc-900">Gdzie opublikować?</h2>
                    </div>
                    <p className="text-[12px] text-zinc-500 mb-4">Wybraliśmy najlepsze grupy dla Twojej oferty</p>

                    {/* Simple 3-step guide */}
                    <div className="flex items-center gap-3 mb-4 px-1">
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-[#F47A3B] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                        <span className="text-[11px] text-zinc-600">Dołącz do grupy</span>
                      </div>
                      <ChevronRight className="w-3 h-3 text-zinc-300" />
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-[#F47A3B] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                        <span className="text-[11px] text-zinc-600">Zaznacz</span>
                      </div>
                      <ChevronRight className="w-3 h-3 text-zinc-300" />
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-[#F47A3B] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                        <span className="text-[11px] text-zinc-600">Publikuj</span>
                      </div>
                    </div>

                    {/* Groups — categorized */}
                    {groups.length > 0 && (() => {
                      const bestGroups = groups.slice(0, 3);
                      const otherGroups = groups.slice(3);
                      return (
                        <div className="space-y-4 mb-4" data-testid="pub-groups-list">
                          {/* Best groups */}
                          <div>
                            <p className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider mb-2">Najlepsze dla tej oferty</p>
                            <div className="space-y-2">
                              {bestGroups.map((g) => (
                                <GroupRow key={g.id} g={g} isPosted={postedGroups.has(g.id)} isSelected={selectedGroups.has(g.id)} onToggle={toggleGroup} onDelete={handleDeleteGroup} />
                              ))}
                            </div>
                          </div>
                          {/* Other groups */}
                          {otherGroups.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Opcjonalne</p>
                              <div className="space-y-2">
                                {otherGroups.map((g) => (
                                  <GroupRow key={g.id} g={g} isPosted={postedGroups.has(g.id)} isSelected={selectedGroups.has(g.id)} onToggle={toggleGroup} onDelete={handleDeleteGroup} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Add group — prominent */}
                    <AnimatePresence>
                      {addingGroup ? (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                          className="p-4 bg-[#F8FAFC] rounded-xl border border-zinc-200 mb-4">
                          <div className="flex gap-2 mb-2">
                            <Input placeholder="Nazwa grupy" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} className="text-[13px] rounded-lg" data-testid="new-group-name" />
                            <Input placeholder="Link do grupy" value={newGroupUrl} onChange={e => setNewGroupUrl(e.target.value)} className="text-[13px] rounded-lg flex-[2]" data-testid="new-group-url" />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={handleSaveGroup} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-lg text-[12px]" data-testid="save-group-btn">Zapisz</Button>
                            <Button onClick={() => setAddingGroup(false)} size="sm" variant="ghost" className="text-[12px]">Anuluj</Button>
                          </div>
                        </motion.div>
                      ) : (
                        <button
                          onClick={() => setAddingGroup(true)}
                          className="w-full flex items-center justify-center gap-2 py-3 mb-4 rounded-xl border-2 border-dashed border-zinc-300 bg-white hover:border-[#F47A3B]/40 hover:bg-[#FFF7ED] transition-all text-[13px] font-semibold text-zinc-500 hover:text-[#F47A3B]"
                          data-testid="add-group-toggle"
                        >
                          <Plus className="w-4 h-4" /> Dodaj swoją grupę
                        </button>
                      )}
                    </AnimatePresence>

                    {/* Suggestions */}
                    {suggestions.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[12px] font-semibold text-zinc-500 mb-2">Polecane grupy — dołącz i publikuj</p>
                        <div className="space-y-2">
                          {suggestions.slice(0, 4).map((s, i) => {
                            const meta = REASON_META[s.reason] || REASON_META.ogólna;
                            const Icon = meta.icon;
                            return (
                              <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAFBFC] border border-dashed border-zinc-200">
                                <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[12px] font-semibold text-zinc-900">{s.name}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ color: meta.color, background: `${meta.color}12` }}>{meta.badge}</span>
                                    {s.members && <span className="text-[9px] text-zinc-400">{fmtMembers(s.members)} czł.</span>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <Button size="sm" onClick={() => { handleAddSuggestion(s); if (s.url) window.open(s.url, '_blank'); }}
                                    className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-lg text-[11px] h-7 font-semibold"
                                    data-testid={`join-suggestion-${i}`}
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" /> Dołącz
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  </div>
                </motion.div>
              )}

              {/* ═══ FINAL CTA ═══ */}
              {post && selectedGroupsList.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} data-testid="pub-final-cta">
                  <Button
                    onClick={handleStartPublishing}
                    className="w-full rounded-xl h-12 text-[15px] font-bold text-white shadow-lg shadow-[#F47A3B]/20"
                    style={{ background: 'linear-gradient(135deg, #F47A3B, #E06A2B)' }}
                    data-testid="pub-final-publish"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Opublikuj w wybranych grupach
                  </Button>
                  <p className="text-[11px] text-zinc-400 text-center mt-2">
                    Asystent poprowadzi Cię krok po kroku
                  </p>
                </motion.div>
              )}

              {/* ═══ No photos ═══ */}
              {!hasPhotos && !generating && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <div className="bg-white rounded-2xl border border-zinc-200 p-6 text-center" data-testid="pub-no-photos">
                    <Camera className="w-10 h-10 text-[#F47A3B] mx-auto mb-3" />
                    <h2 className="text-[16px] font-bold text-zinc-900 mb-1">Dodaj zdjęcia, aby system przygotował post</h2>
                    <p className="text-[13px] text-zinc-500 mb-4">Posty ze zdjęciami dostają więcej zapytań</p>
                    <Button onClick={() => navigate('/panel/profil?tab=photos')} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl">
                      <Camera className="w-4 h-4 mr-2" /> Dodaj zdjęcia w profilu
                    </Button>
                  </div>
                </motion.div>
              )}

            </div>
          </div>

          {/* ═══ RIGHT COLUMN ═══ */}
          <ContextPanel
            title={t('publish.contextTitle')}
            desc={t('publish.contextDesc')}
            benefits={[
              t('publish.contextBenefit1'),
              t('publish.contextBenefit2'),
              t('publish.contextBenefit3'),
            ]}
            action={t('publish.contextAction')}
            link={{ to: '/panel/wyniki', title: t('publish.seeResults'), subtitle: t('publish.checkInquiries') }}
            icon={Send}
          />
        </div>
      </div>

      {/* ═══ MODALS ═══ */}

      {/* Edit Text */}
      <AnimatePresence>
        {showEditText && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) setShowEditText(false); }}
            data-testid="edit-text-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-[90vw] max-w-[560px] overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-[#F47A3B]" />
                  <h2 className="text-[16px] font-bold text-zinc-900">Edytuj tekst</h2>
                </div>
                <button onClick={() => setShowEditText(false)} className="text-zinc-400 hover:text-zinc-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5">
                <textarea
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  className="w-full h-48 border border-zinc-200 rounded-xl p-4 text-[14px] text-zinc-800 resize-none focus:border-[#F47A3B] focus:ring-1 focus:ring-[#F47A3B]/20 outline-none"
                  data-testid="edit-text-area"
                />
                <div className="flex gap-3 mt-4">
                  <Button onClick={handleSaveText} className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl flex-1" data-testid="save-text-btn">Zapisz zmiany</Button>
                  <Button onClick={() => setShowEditText(false)} variant="outline" className="rounded-xl">Anuluj</Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Picker */}
      {showMediaPicker && (
        <MediaPicker open={showMediaPicker} onSelect={handleMediaSelect} onClose={() => setShowMediaPicker(false)} />
      )}

      {/* Version Picker */}
      <AnimatePresence>
        {showVersions && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) setShowVersions(false); }}
            data-testid="version-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-[90vw] max-w-[400px] overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-[#F47A3B]" />
                  <h2 className="text-[16px] font-bold text-zinc-900">Wybierz wersję</h2>
                </div>
                <button onClick={() => setShowVersions(false)} className="text-zinc-400 hover:text-zinc-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-2">
                {[
                  { id: 'sprzedazowy', label: 'Sprzedażowa', desc: 'Bezpośredni przekaz — kup teraz', icon: '🔥' },
                  { id: 'emocjonalny', label: 'Emocjonalna', desc: 'Historia i emocje — buduje zaufanie', icon: '❤️' },
                  { id: 'krotki', label: 'Krótka', desc: 'Zwięzła — max 3 zdania', icon: '⚡' },
                ].map(v => (
                  <button
                    key={v.id}
                    onClick={() => handleVersion(v.id)}
                    disabled={generating}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-zinc-200 hover:border-[#F47A3B]/30 hover:bg-[#FFF7ED] transition-all text-left"
                    data-testid={`version-${v.id}`}
                  >
                    <span className="text-xl">{v.icon}</span>
                    <div className="flex-1">
                      <p className="text-[14px] font-semibold text-zinc-900">{v.label}</p>
                      <p className="text-[11px] text-zinc-500">{v.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-400" />
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Publish Wizard */}
      <AnimatePresence>
        {showWizard && groups.length > 0 && post && (
          <PublishWizard
            groups={selectedGroupsList.length > 0 ? selectedGroupsList : groups}
            post={post}
            onClose={() => setShowWizard(false)}
            onMarkPosted={handleMarkPosted}
            onFinish={() => { setShowWizard(false); navigate('/panel/wyniki'); }}
          />
        )}
      </AnimatePresence>

      {/* Paywall */}
      {showPaywall && (
        <Paywall
          onClose={() => setShowPaywall(false)}
          groupsCount={selectedGroups.size}
          subscription={subscription}
          context="publish"
        />
      )}

      {/* ═══ STICKY CTA — always visible when post exists ═══ */}
      {post && !showWizard && !showEditText && !showMediaPicker && !showVersions && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-zinc-200 px-4 py-3 md:pl-[260px]" data-testid="sticky-cta">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-1 min-w-0 hidden sm:block">
              <p className="text-[13px] font-semibold text-zinc-800 truncate">
                {post.text ? t('publish.postReady') : t('publish.createPost')}
              </p>
              <p className="text-[11px] text-zinc-400">Opublikuj teraz — to pierwszy krok do zapytań</p>
            </div>
            <Button
              onClick={handleStartPublishing}
              disabled={selectedGroupsList.length === 0}
              className="rounded-xl px-6 py-5 text-[14px] font-bold text-white shadow-lg shadow-[#F47A3B]/20 whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #F47A3B, #E06A2B)' }}
              data-testid="sticky-publish-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Opublikuj teraz
            </Button>
          </div>
        </div>
      )}

    </PanelLayout>
  );
}
